import { guardAdminOnly } from "@/lib/auth/routeGuards"
import { useState, useEffect } from 'react'
import { cn } from '@/lib/utils'
import {
  Home,
  Users,
  Building,
  ArrowLeftCircle,
  UserCheck,
  UserPlus,
  User,
  BookOpen,
  GraduationCap,
  School,
  Layers,
} from 'lucide-react'
import { createFileRoute, Outlet, useSearch } from '@tanstack/react-router'
import { useNavigate } from '@tanstack/react-router'
import EliminationOverview from '@/components/adminPage/student/EliminationOverView'
import StudentManagement from '@/components/adminPage/student/StudentManagement'
import TeacherManagement from '@/components/adminPage/teacher/TeacherManagement'
import ClassManagement from '@/components/adminPage/etablissment/ClassManagment'
import SubjectManagement from '@/components/adminPage/etablissment/Subject'
import ParentManagment from '@/components/adminPage/parent/ParentManagment'
import ListEliminate from '@/components/adminPage/student/ListEliminate'
import CalculateStudentScore from '@/components/adminPage/student/CalculateStudentScore'
import { ClassroomManagement } from '@/components/adminPage/classroom/ClassroomManagement'
import { GradeManagement } from '@/components/adminPage/etablissment/GradeManagement'
import { useTranslation } from 'react-i18next'
import { DatastorageAdminTour } from '@/tour/admin/datastorageAdminTour'

export const Route = createFileRoute('/admin/database')({
  beforeLoad: guardAdminOnly,
  component: RouteComponent,
})

export default function RouteComponent() {
  const { t } = useTranslation()
  const [activeComponent, setActiveComponent] = useState('Overview')
  const [activeSubComponent, setActiveSubComponent] = useState('')
  const navigate = useNavigate()
  const search = useSearch({ from: '/admin/database' }) as { section?: string }

  useEffect(() => {
    if (search.section) {
      const parentComponent = getNavItems(t).find((item) =>
        item.nested?.some(
          (nestedItem) => nestedItem.component === search.section
        )
      )

      if (parentComponent) {
        setActiveComponent(parentComponent.component)
        setActiveSubComponent(search.section)
      } else {
        setActiveComponent(search.section)
        setActiveSubComponent('')
      }
    }
  }, [search.section, t])

  const componentsMap: { [key: string]: JSX.Element } = {
    Overview: <EliminationOverview />,
    Users: <EliminationOverview />,
    TeacherManagement: <TeacherManagement />,
    StudentManagement: <StudentManagement />,
    ParentManagement: <ParentManagment />,
    Establishment: <ClassManagement />,
    ClassManagement: <ClassManagement />,
    ClassroomManagement: <ClassroomManagement />,
    SubjectManagement: <SubjectManagement />,
    GradeManagement: <GradeManagement />,
    Elimnate: <EliminationOverview />,
    ListEliminate: <ListEliminate />,
    CalculateStudentScore: <CalculateStudentScore />,
  }

  function goBack() {
    navigate({ to: '/admin' })
  }

  return (
    <div className="flex h-[calc(120vh+100px)]">
      <Sidebar
        setActiveComponent={setActiveComponent}
        setActiveSubComponent={setActiveSubComponent}
        goBack={goBack}
        activeComponent={activeComponent}
        activeSubComponent={activeSubComponent}
        navigate={navigate}
      />
      <div className="flex-1 p-6 overflow-auto">
        {componentsMap[activeSubComponent || activeComponent]}
        <Outlet />
        <DatastorageAdminTour />
      </div>
    </div>
  )
}

function getNavItems(t: any) {
  return [
    {
      label: t('routes.admin-folder.database.navigation.overview'),
      component: 'Overview',
      icon: Home,
    },
    {
      label: t('routes.admin-folder.database.navigation.users.title'),
      component: 'Users',
      icon: Users,
      nested: [
        {
          label: t(
            'routes.admin-folder.database.navigation.users.studentManagement'
          ),
          component: 'StudentManagement',
          icon: UserPlus,
        },
        {
          label: t(
            'routes.admin-folder.database.navigation.users.parentManagement'
          ),
          component: 'ParentManagement',
          icon: User,
        },
        {
          label: t(
            'routes.admin-folder.database.navigation.users.teacherManagement'
          ),
          component: 'TeacherManagement',
          icon: UserCheck,
        },
       
      ],
    },
    {
      label: t('routes.admin-folder.database.navigation.establishment.title'),
      component: 'Establishment',
      icon: Building,
      nested: [
        {
          label: t(
            'routes.admin-folder.database.navigation.establishment.classManagement'
          ),
          component: 'ClassManagement',
          icon: GraduationCap,
        },
        {
          label: t(
            'routes.admin-folder.database.navigation.establishment.gradeManagement'
          ),
          component: 'GradeManagement',
          icon: School,
        },
        {
          label: t(
            'routes.admin-folder.database.navigation.establishment.classroomManagement'
          ),
          component: 'ClassroomManagement',
          icon: Layers,
        },
        {
          label: t(
            'routes.admin-folder.database.navigation.establishment.subjectManagement'
          ),
          component: 'SubjectManagement',
          icon: BookOpen,
        },
      ],
    },
  ]
}

function Sidebar({
  setActiveComponent,
  setActiveSubComponent,
  goBack,
  activeComponent,
  activeSubComponent,
  navigate,
}: {
  setActiveComponent: (component: string) => void
  setActiveSubComponent: (component: string) => void
  goBack: () => void
  activeComponent: string
  activeSubComponent: string
  navigate: any
}) {
  const { t } = useTranslation()
  const navItems = getNavItems(t)

  return (
    <div
      className={cn(
        'h-full w-64 bg-gray-100 border-r border-gray-200 flex flex-col'
      )}
    >
      <div className="p-4 space-y-4">
        <button
          onClick={goBack}
          className="flex items-center gap-2 p-2 rounded-lg text-gray-700 hover:bg-gray-200 hover:text-gray-900 w-full text-left font-medium"
        >
          <ArrowLeftCircle className="h-5 w-5 text-gray-700" />
          <span>{t('routes.admin-folder.database.navigation.back')}</span>
        </button>

        {navItems.map(({ label, component, icon: Icon, nested }) => (
          <div key={component}>
            <button
              onClick={() => {
                setActiveComponent(component)
                if (component === "Users") {
                  setActiveSubComponent("StudentManagement")
                  navigate({
                    search: (prev: any) => ({
                      ...prev,
                      section: "StudentManagement",
                    }),
                  })
                } else if (component === "Establishment") {
                  setActiveSubComponent("ClassManagement")
                  navigate({
                    search: (prev: any) => ({
                      ...prev,
                      section: "ClassManagement",
                    }),
                  })
                } else {
                  setActiveSubComponent('')
                  navigate({
                    search: (prev: any) => ({
                      ...prev,
                      section: component,
                    }),
                  })
                }
              }}
              className={cn(
                'flex items-center gap-2 p-2 rounded-lg w-full text-left font-medium',
                activeComponent === component
                  ? 'bg-primary text-white'
                  : 'text-gray-700 hover:bg-gray-200 hover:text-gray-900'
              )}
            >
              <Icon className="h-5 w-5" />
              <span>{label}</span>
            </button>

            {nested && activeComponent === component && (
              <div className="ml-6 mt-2 space-y-2">
                {nested.map(({ label, component, icon: Icon }) => (
                  <button
                    key={component}
                    onClick={() => {
                      setActiveSubComponent(component)
                      setActiveComponent(activeComponent)
                      navigate({
                        search: (prev: any) => ({
                          ...prev,
                          section: component,
                        }),
                      })
                    }}
                    className={cn(
                      'flex items-center gap-2 p-2 rounded-lg w-full text-left text-sm',
                      activeSubComponent === component
                        ? 'bg-primary text-white'
                        : 'text-gray-700 hover:bg-gray-200 hover:text-gray-900'
                    )}
                  >
                    <Icon className="h-5 w-5" />
                    <span>{label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
